import { useContext, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { NextRouter, withRouter } from 'next/router';
import formIds from 'src/app_config/sitecore-form-ids.json';

import { SitecoreForm as SitecoreFormType } from '@sitecore-jss/sitecore-jss-forms';
import { createDefaultFieldFactory, Form } from '@sitecore-jss/sitecore-jss-react-forms';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ModalContext } from 'src/context/Modal/ModalContext';

import { SitecoreContextValue } from 'src/lib/ComponentProps';

import { ReCaptchaFieldProps } from 'src/types/ReCaptchaFieldProps';
import { PrefillRegisterFormInputs, RegisterFormCustomInputs } from 'src/types/RegisterForm';

import { handleFieldOnChange } from 'src/utils/handleFieldOnChange';
import { SitecoreFormFieldIds } from 'src/utils/SitecoreFormFieldIds';

import styles from './SitecoreForm.module.css';

type SitecoreFormProps = {
	fields: SitecoreFormType;
	router: NextRouter;
	prefillRegisterFormInputs?: PrefillRegisterFormInputs;
	registerFormCustomInputs?: RegisterFormCustomInputs;
};

const customFormFactory = createDefaultFieldFactory();

customFormFactory.setComponent(SitecoreFormFieldIds.hidden, (props) => {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const ModalState = useContext(ModalContext);

	const valueField = props.field.valueField as { name: string };

	if (valueField.name === formIds.RegisterYourInterest.courseFieldName) {
		props.field.model.value = sitecoreContext.route?.fields?.PageTitle.value;
	}

	if (valueField.name === formIds.RegisterYourInterest.recipientFieldName) {
		if (ModalState.modalInfo?.CourseRegisterInterestModal?.Data) {
			const recipient = ModalState.modalInfo.CourseRegisterInterestModal.Data['recipient'];
			if (recipient && recipient !== '') {
				props.field.model.value = recipient;
			} else {
				console.error(
					"Register Interest Modal - unable to send email as there is no email address set in the 'RegisterInterestEmailRecipient' field on the Course"
				);
			}
		}
	}

	return <input type="hidden" value={props.field.model.value as string} name={valueField.name} />;
});

customFormFactory.setComponent(SitecoreFormFieldIds.rawHTML, (props) => {
	return props.field.model.html ? <div dangerouslySetInnerHTML={{ __html: `${props.field.model.html}` }} /> : <></>;
});

customFormFactory.setComponent(SitecoreFormFieldIds.reCaptcha, (props: ReCaptchaFieldProps) => {
	const {
		field: {
			model: { captchaPublicKey },
			valueField: { name },
		},
		onChange,
	} = props;

	const formattedName = name && !name.includes('CaptchaValue') ? name.replace('Value', 'CaptchaValue') : name;

	return captchaPublicKey && name ? (
		<div className={styles.recaptchaContainer}>
			<ReCAPTCHA
				className={styles.recaptcha}
				sitekey={captchaPublicKey}
				onChange={(e: string) => handleFieldOnChange(formattedName, e, 'Please confirm you are not a robot.', onChange)}
			/>
		</div>
	) : (
		<></>
	);
});

const SitecoreForm = (props: SitecoreFormProps) => {
	const formWrapper = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const form = formWrapper.current?.querySelector('form');
		const submitBtn = form?.querySelector('[type="submit"]');

		if (!form || !submitBtn) {
			return;
		}

		const obs = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				for (const el of mutation.removedNodes) {
					if (el === submitBtn) {
						obs.disconnect();
					}
				}
			}
		});

		obs.observe(form, {
			childList: true,
		});

		return () => obs.disconnect();
	}, []);

	return (
		<section className="maxWidth">
			<div ref={formWrapper} className={styles.formWrapper}>
				<Form
					form={props.fields}
					sitecoreApiHost={''}
					// RCN - pull Api Key from env variables
					sitecoreApiKey={process.env.NEXT_PUBLIC_SITECORE_API_KEY ?? 'FAILED-TO-GET-API-KEY'}
					fieldFactory={customFormFactory}
					onRedirect={(url) => props.router.push(url)}
					className={styles.form}
				/>
			</div>
		</section>
	);
};

export default withRouter(SitecoreForm);
